import _ from 'lodash'
import * as d3 from 'd3-selection'
import EventEmitter from 'events'
import { HIDE_ON_VIEW_CLASS } from './header-cell'
import TableHead from './table-head'
import TableDnd from './table-dnd'
import SingleColumnStack from './single-column-stack'
import icon from 'scoreboard/components/icon'
import i18n from 'scoreboard/components/i18n'
import addFirstHint from 'scoreboard/components/add-first-hint.hbs'
import loadingDotsTbody from 'scoreboard/components/loading-dots-table-body.hbs'
import LoadingDots from 'scoreboard/components/loading-dots'
import reportConstants from 'scoreboard/reports/report-constants'
import conductor from 'scoreboard/components/conductor'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import nonAbbreviatedValuesTooltip from 'scoreboard/components/non-abbreviated-values-tooltip.hbs'
import guard from 'scoreboard/components/unsaved/guard'
import tableMessageTemplate from './table-message.hbs'
import ServerErrorDetailsDialog from 'scoreboard/components/server-error-details-dialog'
import appDisplay from 'scoreboard/components/app-display'
import $ from 'jquery'

const settableStackAttributes = ['rowspan', 'colspan']

const emptyStyle = {'is-empty': true}

const eventSplitter = /^(\S+)\s*(.*)$/ //same as backbone's syntax

function emptyCell() {
	return {style: {'is-empty': true}}
}

const LAST_STACK_ADDABLE_CLASS = 'last-stack-addable'
const DND_HEADER_CLASS = 'dnd-header'

function addGroupingMargin(data, grouping) {
	//if we're not grouping, don't add anything
	if (!grouping) { return data }
	let emptyCells = []
	//if this is a subgroup indent by 2
	let numberOfCells = grouping.isSubgroup() ? 2 : 1
	emptyCells = _.times(numberOfCells, () => {
		return emptyCell()
	})
	//use a new array to prevent altering the stored array in the header
	return _.map(data, row => [...emptyCells].concat(row) )
}

function emptyTableHead() {
	let emptyStack = (index, attr = {}, opts = {}) => {
		let columnAttributes = _.extend({style: {'empty-header': true}}, attr)
		let stackFormatter = {toCellContents: () => { return emptyCell() } }
		let stackOpts = _.extend({immovable: true, addable: false, editable: false,
			dataExtractor: stackFormatter}, opts)
		return new SingleColumnStack(index, columnAttributes, stackOpts)
	}

	let addFirstHintContent = {
		html: addFirstHint({
			hint: i18n.get('report.designer.addFirstColumn'),
			downwardArrow: true
		})
	}

	return new TableHead([emptyStack(0, addFirstHintContent, {addable: true}),
		emptyStack(1), emptyStack(2)])
}

function joinObjectToString(obj, sep = ' ') {
	if (_.isString(obj)) {
		return obj
	}
	return _.reduce(obj, (result, val, key) => {
		if (val) { result += `${key} ` }
		return result
	}, '')
}

function setContent(selection) {
	//must not use lambda, we want the "this" binding to refer to the element
	selection
		.attr('class', (d,i) => {
			if (_.isNil(d)) {
				return null
			}
			let style = ''
			if (!_.isNil(d.style)) {
				if (_.isFunction(d.style)) {
					style = joinObjectToString(d.style())
				} else {
					style = joinObjectToString(d.style)
				}
			}
			return style
		})
		.attr('data-value-type', d => d.dataValueType)
		.html(function(d) {
			//store the export model on the domNode itself
			if (d.exportModel) {
				this.exportModel = d.exportModel
			}

			let prefixIcon = ''
			if (this.classList.contains(HIDE_ON_VIEW_CLASS)) {
				prefixIcon = icon('hide')
			}
			if (d.inlineStyle) {
				_.each(d.inlineStyle, (val, name) => {
					this.style.setProperty(name,val)
				})
			}
			//#24857 requested by derek to wrap contents in div
			return `<div class="content">${prefixIcon}${d.html || ''}</div>`
		})
	_.each(settableStackAttributes, attr => {
		selection.attr(attr, d => d[attr] || null)
	})
	return selection
}

function joinByProperty(property) {
	return (d,idx) => {
		let val = _.get(d, property, idx)
		if (_.isFunction(val)) {
			return _.invoke(d, property)
		}
		return val
	}
}


function appendAddMarker(className, selection) {
	selection
		.select('.content')		//#24857 requested by derek for styling
			.append('div')
			.html(icon('diamond-add'))
			.classed(className, true)
			.on('click', function (e, d) {
				e.stopPropagation()
				if (!d.addable) return
				d.emit('add', d, this)
			})

	_.each(selection.nodes(), node => {
		node._parent.classList.add('has-stack-addable')
	})
}

class Table extends EventEmitter {

	/**
	 * @param opts.model {Backbone.Model} the report model
	 */
	constructor(tableStructure= [], tableNode, opts = {}) {
		super()
		this.tableOpts = _.extend({editable: true, dnd: true}, opts)
		this._tableStructure = {}
		this._tableState = {}
		this._table = d3.select(tableNode)
		this.updateTableStructure(tableStructure, opts)

		this.editable = this.tableOpts.editable
		this._dnd = this.editable && this.tableOpts.dnd

		//setup the loading dots
		this.loadingDots = new LoadingDots()
		//certain views (like a dashboard report) may want to handle the loading
		//dots, so provide a way to disable the table managed loading dots
		if (!this.tableOpts.disableLoadingDots) {
			this.loadingDots.on('render', () => {
				this._isLoading = true
				this.editable = false	//while we're loading, disable editing
				this.selection().selectAll(`tbody`).remove()
				this.selection().html(() => loadingDotsTbody())

				let el = this.selection().classed('editable', false)
					.classed('has-loading-dots', true)
					.select('tbody.loading-dots-tbody tr')
					.classed('data-row', true)
					.classed('is-not-displayed', false)
					.select('td')
					.classed('data-cell', true)
					.node()
				this.loadingDots.setElement(el)
			})
		}
	}

	/**
	 * Sets the table structure for the table, callers must call render to update
	 * the display.  This separation allows multiple updates to happen without
	 * re-rendering the display until it is necessary (which should avoid
	 * unnecessary computation work by the browser in redrawing the table)
	 *
	 * @param {Array} tableStructure array of table structure data, each element
	 * corresponds to one grouping of data.  If the data is not grouped in any
	 * way, this array should just consist of one element
	 *
	 * @param {Object} [opts.append] true if we're append rows.
	 * @return {Table}
	 */
	updateTableStructure(tableStructure = [], opts = {}) {
		this._table.classed('has-selected-stack', false)
		let highestVisibleOuterGroupIndex = 0
		let groupsToRender = new Set()

		if (opts.append) {
			//#43858 when appending we need to account for the existing structure
			_.forEach(this._tableStructure, (structure, groupIndexKey) => {
				groupsToRender.add(groupIndexKey)
			})
		}

		_.each(tableStructure, (structure, index) => {
			let {highestOuterGroupIndex, groupIndexKey}  = this._parseTableStructure(structure, index, opts)
			//determines the highest visible group based on the data given in the
			//structure. This is necessary to prevent rendering a bunch of table
			//groups whose data has not yet been received by the table.  This happens
			//because the data results can be streamed to the table, in contrast
			//to the entire table structure being able to be set at one time
			highestVisibleOuterGroupIndex = Math.max(highestVisibleOuterGroupIndex,
				highestOuterGroupIndex)
			groupsToRender.add(groupIndexKey)
		})

		//remove existing group that are no longer in the structure
		_.forEach(this._tableStructure, (structure, groupIndexKey) => {
			if (!groupsToRender.has(groupIndexKey)) {
				delete this._tableStructure[groupIndexKey]
			}
		})

		//check if no headers have been defined anywhere, if so show the empty
		//table state
		let emptyHeaders = _.isEmpty(tableStructure) ||
			_.every(this._tableStructure, structure => {
				let header = structure.updateHeader || structure.header
				return _.isNil(header) || header.stacks().length == 0
			})
		if (emptyHeaders) {
			let groupIndexKey = reportConstants.DEFAULT_GROUP_INDEX_KEY
			this._tableStructure[groupIndexKey] = {
				updateHeader: emptyTableHead(),
				updateData: [{},{},{}],
				structureIndex: 0,
				groupIndexKey,
				opts
			}
		}
		this._tableState.emptyHeaders = emptyHeaders

		//check if the table has any groupings defined
		let noGrouping = _.every(this._tableStructure, structure => {
			let groupings = structure.updateGrouping || structure.grouping
			return _.isNil(groupings) || groupings.length == 0
		})

		//check if any data was present
		let noResults = _.every(this._tableStructure, structure => {
			const { updateData } = structure
			return _.isNil(updateData) || _.isEmpty(structure.updateData)
		})

		this._tableState.noResults = noResults
		this._tableState.noGrouping = noGrouping
		this._tableState.highestVisibleOuterGroupIndex = highestVisibleOuterGroupIndex
		return this
	}

	_parseTableStructure(structure, structureIndex, opts = {}) {
		let highestOuterGroupIndex = 0
		let {grouping, header, data, aggregating} = structure
		//use the default group index key if not defined
		let groupIndexKey = structure.groupIndexKey || reportConstants.DEFAULT_GROUP_INDEX_KEY

		let parsedStructure = this._tableStructure[groupIndexKey] || {}
		parsedStructure.opts = opts
		parsedStructure.aggregating = aggregating
		parsedStructure.structureIndex = structureIndex
		parsedStructure.groupIndexKey = groupIndexKey

		let removeEmptyHeader = false
		//for each type of section (grouping/header/data) we have a updated
		//property that stores the updates that need to be rendered, this
		//allows for selective updating the table in pieces by passing in
		//only the sections that need updating
		if (header) {
			parsedStructure.updateHeader = new TableHead(header, this.tableOpts)
			removeEmptyHeader = !!this._tableState.emptyHeaders		//#39256
			if (removeEmptyHeader) {
				//#40172 remove the fake data associated with the "empty header" state
				parsedStructure.data = data
			}
		}
		if (grouping) {
			parsedStructure.updateGrouping = grouping
		}
		if (data || removeEmptyHeader) {
			let parsedData = parsedStructure.data || []
			if (data) {
				if (opts.append) {
					parsedData = parsedData.concat(data)
				} else {
					parsedData = data
				}
			}
			parsedStructure.updateData = parsedData

			//find the highest outer group index, used to chunk the displaying of groups
			highestOuterGroupIndex = _(parsedData).map(reportItem => reportItem.outerGroupIndex || 0).max()
		}

		//keep a numerical order for proper sorting #46904
		parsedStructure.order = parseFloat(parsedStructure.groupIndexKey)

		this._tableStructure[groupIndexKey] = parsedStructure
		return { highestOuterGroupIndex, groupIndexKey }
	}

	render(opts = {}) {
		this.loadingDots.clear()
		//set editability based on how the table was initialized as (since
		//editability can change temporary when loading)
		this.editable = _.get(this.tableOpts,'editable', true)
		this.selection().classed('editable', this.editable)
			.classed('has-loading-dots', false)
		if (this._isLoading) {
			delete this._isLoading
			this.selection().select('tbody.loading-dots-tbody').remove()
		}

		//create the overall tbody structure
		this._renderTableStructure()

		//for each grouping, render the grouping/headers/data
		_.each(this._tableStructure, structure => {
			//if we're not grouping, we can rely on the data stream to
			//implement how much data is fed to the table.  However because
			//of the way streaming records and grouping is implemented, we'll
			//need to handle this in the table itself by limiting the
			//amount of groupings rendered based on the "highestVisibleOuterGroupIndex"
			//calculation when parsing the table structure.
			if (this._tableState.noGrouping ||
				structure.grouping.outerGroupIndex <= this._tableState.highestVisibleOuterGroupIndex) {
				this._renderHeaders(structure)
				//render the headers first, since the grouping heads will span
				//over the number of headers in the table
				this._renderGrouping(structure)
				this._renderData(structure, opts)
			}
		})

		//when the table is editable we need to assign the cells in stack
		//so the necessary styles can be applied when the stacks are manipulated
		if (this.editable) {
			//need to clear previous bound body nodes each time data is rendered
			let primaryHeaderStacks = this.header().stacks()
			_.invokeMap(primaryHeaderStacks, 'resetBodyDomNodes')

			//find all the body nodes and bind them to their respective editable stack
			this.selection().selectAll('.dom-body').selectAll(function(d,i) {
				if (d.tableStack) {
					d.tableStack && d.tableStack.bindStackBodyDomNode(this, d)
				}
			})

			// clear the previously bound dom nodes for the grouped header cells
			_.each(this._tableStructure, structure => {
				const grouping = structure.grouping
				if (grouping && grouping.stack) {
					grouping.stack.resetHeaderDomNodes()
				}
			})
			this.selection().selectAll('.grouping-cell').selectAll(function(d,i) {
				d.stack && d.stack.bindStackHeaderDomNode(this, d)
			})

			_.each(primaryHeaderStacks, col => {
				this._propagateEvents(col, ['selectCell', 'selectAdvancedLayoutCell'])
			})
		}

		//setup dnd
		if (this._dnd) {
			this._tableDnd = new TableDnd(this)
		}

		//add tooltip to show unshortened values
		_.invoke(this.nonAbbreviatedValuesTooltip, 'remove')
		// Use the entire cell as the tooltip selector
		let abbreviatedSpan = this.selection().selectAll('span[data-unshortened-value]').nodes()
		let abbreviatedTooltipSelector = $(abbreviatedSpan).closest('td')
		this.nonAbbreviatedValuesTooltip = new TooltipAndDetails({
			el: this.selection().node(),
			selector: abbreviatedTooltipSelector,
			tooltipContent: (e) => {
				let content = $(e.currentTarget).find('.content')
				let children = content.children(':not(span.labeled-value-label)')
				let value = children.length > 1 ? '' :
					content[0].querySelector('span:not(.labeled-value-label)')?.dataset['unshortenedValue']
				// If more than one value is in the cell, display all of the
				// unabbreviated values in the tooltip.
				if (children.length > 1) {
					children.each((i, val) => {
						let cloneVal = $(val).clone()
						let unshortened = cloneVal.find('span[data-unshortened-value]')
						unshortened.replaceWith(`<span>${unshortened.data('unshortened-value')}<span>`)
						value += cloneVal[0].outerHTML
					})
				}
				return nonAbbreviatedValuesTooltip({
					header: content[0].querySelector('span:not(.labeled-value-label)')?.dataset['unshortenedHeader'],
					value
				})
			},
			tooltipOptions: {
				extraClass: 'tooltip-and-details non-abbreviated-tooltip'
			},
			preventTooltip: opts.preventTooltip
		})

		//https://github.com/SpiderStrategies/Scoreboard/issues/42604#issuecomment-991705408
		_.invoke(this._onHeaderHoverTooltip, 'remove')
		this._onHeaderHoverTooltip = new TooltipAndDetails({
			el: this.selection().node(),
			selector: '.header-cell.hover-tooltip, .grouping-cell.hover-tooltip',
			tooltipContent: (e) => {
				return e.currentTarget.__data__.onHover(e)
			},
			tooltipOptions: {
				placement: 'above',
				extraClass: 'tooltip'
			}
		})

		return this
	}

	applySpotlight() {
		//apply spotlighting if any
		_.invokeMap(this.header().stacks(), 'applySpotlight')
	}

	_renderTableStructure() {
		//it's easier to manipulate the header and data sections if we split
		//them up into their own tbody groups, this d3 structure emulates that
		let d3DataStructure = []

		//iterate in group index order so that the table structure is displayed correctly
		let groupStructureInOrder = _.sortBy(this._tableStructure, s => s.order)
		_.each(groupStructureInOrder, (structure) => {
			//check if the groupings have been updated first, since they affect
			//table header placement (whether margins need to be added)
			if (structure.updateGrouping) {
				if (structure.grouping) {
					// clear the events emitted by the former header cell
					// we will be attaching the relevant events to the new one
					const headerCell = structure.grouping.stack?.headerCell()
					_.invokeMap([headerCell], 'removeAllListeners')
				}
				structure.grouping = structure.updateGrouping
				delete structure.updateGrouping
			}

			let { grouping, groupIndexKey } = structure
			let outerGroupIndex = 0
			if (grouping) {
				outerGroupIndex = grouping.outerGroupIndex
				d3DataStructure.push({type: 'grouping', outerGroupIndex, groupIndexKey})
			}
			d3DataStructure.push({type: 'header', outerGroupIndex, groupIndexKey})
			d3DataStructure.push({type: 'data', outerGroupIndex, groupIndexKey})
		})
		//generate the tbody groups
		this._tbodies = this.selection().selectAll(`tbody`)
			.data(d3DataStructure, function(d) {
				return d.type + d.groupIndexKey
			})
			.join('tbody')
				.attr('class', (d,i) => { return d.type })
				.attr('data-outer-group-index', (d,i) => { return d.outerGroupIndex})
				.attr('data-group-index-key', (d,i) => { return d.groupIndexKey})
	}

	_renderGrouping(structure) {
		let { grouping, header, groupIndexKey } = structure
		if (_.isNil(grouping)) { return }

		let { stack, innerGroupIndex } = grouping
		let ellipseGrouping
		let ellipseSubGrouping
		let stacksInGrouping = [stack]

		//since each cell needs a formatter, the number of stacks to span will
		//be the number of formatters
		let stackSize = header.stackFormatters().length
		let groupRowData = []
		if (grouping.isSubgroup()) {
			let {outerGrouping} = grouping
			if (innerGroupIndex == 0) {
				//this is the first subgroup, so include the outer group as the
				//first row label
				groupRowData.push([_.defaults({
					colspan: stackSize + 2
				}, outerGrouping)])
				stacksInGrouping.push(outerGrouping.stack)
			}
			groupRowData.push([{style: emptyStyle, stack}, _.defaults({
				colspan: stackSize + 1
			}, grouping)])
			ellipseSubGrouping = grouping.truncated
			ellipseGrouping = outerGrouping.truncated
		} else {
			//just 1 level of grouping
			groupRowData.push([_.defaults({
				colspan: stackSize + 1
			}, grouping)])
			ellipseGrouping = grouping.truncated
		}
		this._addGroupingsRows(groupIndexKey, grouping, groupRowData,
			ellipseGrouping, ellipseSubGrouping)

		//setup listeners on the headers attached to the stacks in the group
		//note cleanup must be done in _renderTableStructure because the group
		//data update happen there
		let events = ['select']
		if (this.editable) {
			events.push('edit')
		}
		_.each(stacksInGrouping, listeningStack => {
			//don't need to worry about calling this on the same stack multiple
			//times as this method prevents setting multiple listeners with the same name
			this._propagateEvents(listeningStack.headerCell(), events)
		})

	}

	_addGroupingsRows(groupIndexKey, group, rowData,
		ellipseGrouping = false, ellipseSubGrouping = false) {
		this._selectTbodySection('grouping', groupIndexKey)
				.classed('last-multiple-grouping', () => {
					return this.editable && ellipseGrouping  //for #38158
				})
				.classed('last-sub-grouping', () => {
					return this.editable && ellipseSubGrouping
				})
			.selectAll('tr.grouping-row')
			.data(rowData)
			.join('tr')
				.classed('grouping-row', true)
			.selectAll('td')
			.data(_.identity)
			.join(enter => {
				//add the grouping cell
				let cell = setContent(enter.append('td'))
				cell.classed('grouping-cell', true)

				//bind the dom node to the grouped stack, this will
				//allow us to listen for events on the grouped header
				cell.each(function(datum, i, d3Group) {
					datum.stack && datum.stack.bindStackHeaderDomNode(this)
				})
			}, update => {
				setContent(update)
				update.classed('grouping-cell', true)

				// bind cells to the updated header cell
				update.each(function(datum, i, d3Group) {
					const headerCell = datum.stack?.headerCell()
					headerCell && headerCell.addDomNode(this)
				})
			})
	}

	_renderHeaders(structure) {
		let update = !!structure.updateHeader
		let table = this.selection()

		//in dashboards, users have the option to hide table headers
		this._selectTbodySection('header').classed('is-not-displayed',
			this.tableOpts.hideHeaders === true)

		//check if a new header was specified since we last rendered, if so
		//we'll need to clean up listening from the old header (if any)
		if (update) {
			if (structure.header) {
				//cleanup on the old existing header if there was one
				//stop listening on the previous stacks, we're setting up new ones
				_.invokeMap(structure.header.stacks(), 'removeAllListeners')
			}

			//set the updated header as the new header
			//the updateHeader will be removed, when we render the body
			structure.header = structure.updateHeader
			delete structure.updateHeader
		}

		let tableHead = structure.header
		if (!tableHead) {
			return //no header, do nothing
		}

		table.classed('colored-cells', true)
		if (!this._tableState.noResults) {
			table.classed('is-clickable clickable-rows', true)
		}
		table.classed('empty-table', this._tableState.emptyHeaders)
		d3.select(table.node().parentNode)
			.classed('has-empty-table', this._tableState.emptyHeaders)

		this._addHeaderRows(structure, tableHead, { update })
	}

	_addHeaderRows(structure, tableHead, opts = {}) {
		let { grouping, groupIndexKey } = structure
		let isFirstStructure = structure.structureIndex == 0
		let data = tableHead.cells()
		let tableHeaderData = addGroupingMargin(data, grouping)
		let rows = this._selectTbodySection('header', groupIndexKey)
			.selectAll('tr.header-row')
			.data(tableHeaderData, function(d, i) {
				return _.invoke(d[i], 'id')	//bind by the header cell's identifier
			})
		rows.exit().remove()

		let enterRows = rows.enter().append('tr')
		enterRows.classed('header-row', true)
			.classed('not-clickable', true)
		rows = rows.merge(enterRows)

		let cells = rows.selectAll('td').data(_.identity, joinByProperty('id')).order()

		//remove non existing stacksgr
		cells.exit().remove()

		//create the headers cells that don't exist
		let newCells = cells.enter().append('td')

		//update/set the contents of exist/new cells
		cells = cells.merge(newCells)
		let domType = isFirstStructure ? DND_HEADER_CLASS : ''
		setContent(cells)
			.classed('header-cell', true)
			.classed(domType, true)

		//if we've generated an updated header, set the dom node reference
		//to each of the updated stacks, otherwise set the reference on new stacks
		let assignDomNodes = opts.update ? cells : newCells
		assignDomNodes.each(function(headerCell) {
			//add the header cell to the collection that is composed of all the
			//cells for the given header in ALL rows
			headerCell.addDomNode && headerCell.addDomNode(this)
		})

		let events = ['select']
		if (this.editable) {
			//setup listeners
			//pass events that are model changes emitted from the stacks
			//unfortunately our browserify uses an old version of the events api
			//so we're listing all of the event here
			events = events.concat(['move', 'add', 'edit', 'drag'])
		}
		let tableHeaderCells = tableHead.cells()
		_(tableHeaderCells).flatMap().each(headerCell => {
			this._propagateEvents(headerCell, events)
		})

		if (this.editable) {
			//only the first group of tbodies shows the add markers
			if (isFirstStructure) {
				let filterByAddability = datum => {
					return datum.addable ? [datum] : []
				}
				let filterByLastAddable = datum => {
					return datum.addable && datum.last ? [datum] : []
				}

				//put addable markers on stacks that allow addability
				let addMarkers = cells.selectAll('div.stack-addable')
					.data(filterByAddability, joinByProperty('id')).order()
				addMarkers.exit().remove()
				//make qualifying cells appendable
				appendAddMarker('stack-addable addable-left', addMarkers.enter().merge(addMarkers))
				appendAddMarker('stack-addable addable-right', addMarkers.enter().merge(addMarkers))

				let lastAddMarker = cells.selectAll(`div.${LAST_STACK_ADDABLE_CLASS}`)
					.data(filterByLastAddable, joinByProperty('id')).order()
				lastAddMarker.exit().remove();
				appendAddMarker(LAST_STACK_ADDABLE_CLASS, lastAddMarker.enter().merge(lastAddMarker))
			}
		}
	}

	_renderData(structure, renderOpts) {
		//determine if we need to display a message instead of the data
		let errorMessage = _.get(structure.opts, 'errorMessage')
		let stackTrace = null
		if (_.isObject(errorMessage)) {
			stackTrace = errorMessage.stackTrace
			errorMessage = errorMessage.message
		}

		let tableMessage = errorMessage || _.get(structure.opts, 'tableMessage')
		if (_.isNil(tableMessage) && this._tableState.noResults) {
			tableMessage = this.tableOpts.noResultsMessage || ''
		}

		if (structure.updateData && structure.header)  {
			structure.data = structure.updateData
			delete structure.updateData
		} else if (_.isNil(tableMessage)) {
			//no data was updated or no header and no table message, do nothing
			return
		}
		let {header, data} = structure

		// if there are no stacks, there could be dummy cells in the table for
		// the "hints" to add columns, so filter out the dummy stacks
		const stacks = _.filter(header.stacks(), s => !_.isNil(s.stack))

		//if we're display a table message instead of the data
		this.selection()
			.classed('has-error', errorMessage)
			.classed('no-data', errorMessage && _.isEmpty(stacks))
		if (tableMessage) {
			this._renderTableMessage(structure, tableMessage,
				header.stackFormatters().length, !_.isNil(errorMessage),
				stackTrace)
			return
		}

		if (_.isFinite(this.tableOpts.rowLimit)) {
			data = data.slice(0, this.tableOpts.rowLimit)
		}
		let formattedData = header.formatByStack(data)

		this._addDataRows(structure, formattedData)
	}

	_addDataRows(structure, formattedData) {
		let { grouping, groupIndexKey } = structure
		//if we're grouping, we'll need to add a cells to left most side that
		//"indent" the grouped rows, this will need to be done on both header
		//and data rows
		formattedData = addGroupingMargin(formattedData, grouping)

		//bind the data to d3 selection
		let rows = this._selectTbodySection('data', groupIndexKey)
			.classed('last-sub-grouping', () => {
				//required both on the 'data' and 'grouping' tbodies
				return this.editable && grouping && grouping.isSubgroup() && grouping.truncated
			})
			.selectAll('tr.data-row')
			.data(formattedData, joinByProperty('id'))

		let hasPreviousData = rows.select('.has-data-message').empty()

		//remove any previous results that are no longer in the data
		rows.exit().remove()

		let { drilldownSection } = this.tableOpts
		let hasDrilldownSection = !_.isNil(drilldownSection)
		// We can drill down if the aggregation represents only one item, but not
		// otherwise, because we wouldn't know which item to drill down on. Every
		// row bears an ID, but that might just be the ID of the first item it
		// represents.
		const numberOfAggregatedRecordsForIndex = index =>
			_.get(structure, `data[${index}].numberOfRecords.value`, index)

		let enterRows = rows.enter().append('tr')
			.classed('data-row', true)
			.classed('not-clickable', (_, index) => {
				const numberOfRecords = numberOfAggregatedRecordsForIndex(index)
				return numberOfRecords !== 1 || !hasDrilldownSection
			})
			.attr('data-id', (datum, index, nodes) => {
				//store the id of the result in the row for drilldowns
				return _.get(structure, `data[${index}].id`, index)
			})

		if (hasDrilldownSection && !this._tableState.noResults) {
			let table = this
			enterRows.on('click', function(e, d, index, nodes) {
				// if something has its own link, we'll respect that
				if ($(e.target).hasClass('sp-navigate')) {
					return
				}

				e.stopPropagation()

				const isDatasets = drilldownSection === 'datasets'
				const doNavigate = isDatasets ?
					numberOfAggregatedRecordsForIndex(index) === 1 :
					table.tableOpts.model.get('showIndividualRecords')
				const drilldownId = isDatasets ? this.dataset.id :
					$(e.target).closest('[data-id]').data('id')

				if (!_.isNil(drilldownId) && doNavigate) {
					let route = `${drilldownSection}/${drilldownId}`
					guard.conditionallyRun(() => conductor.drilldown(route,
						{trigger: true}))
				}
			})
		}

		//function to set the contents of the data cells
		let setDataCellContents = (cells) => {
			setContent(cells)
				.classed('data-cell', true)
				.classed('dom-body', true)
		}

		//extract out the common code in adding content
		let addCells = (cells) => {
			let newDataCells = cells.enter().append('td')
			setDataCellContents(newDataCells)
			//wire any events if defined
			newDataCells.each(function(datum, i) {
				_.each(datum.events, (handler, key) => {
					let [fullText, eventName, selector] = key.match(eventSplitter)
					newDataCells.select(selector).on(eventName, handler)
				})
			})
			return newDataCells
		}

		//determine whether we're appending new rows or updated existing
		//if there were no results in the previous render, we'll do a replace
		//instead of an append to remove the empty message row
		let append = _.get(structure.opts, 'append', false) && hasPreviousData
		let cells
		if (append) {
			cells = enterRows.selectAll('td.data-cell').data(_.identity, joinByProperty('id')).order()
			cells = addCells(cells)
		} else {
			//the merge here will update existing matching rows as specified by the data join
			rows = rows.merge(enterRows)

			//remove any cells associated where the data was removed
			cells = rows.selectAll('td.data-cell').data(_.identity, joinByProperty('id')).order()
			cells.exit().remove()

			//update the contents of existing cells
			setDataCellContents(cells)

			//add any new cells
			let newDataCells = addCells(cells)
			cells = cells.merge(newDataCells)
		}

		//#40687 allow dnd to start from column body (only first structure when grouping)
		let isFirstStructure = structure.structureIndex == 0
		if (isFirstStructure) {
			cells.classed(DND_HEADER_CLASS, true)
		}
	}

	_renderTableMessage(structure, tableMessage, colspan, hasError, stackTrace) {
		let extraClass = hasError ? 'has-error' : ''
		let message = hasError ?
			`${icon('widget-empty-state', 'icon-empty-state')}
			${i18n.get(tableMessage)}` :
			i18n.get(tableMessage)
		let events = null
		if (stackTrace) {
			events = {
				'click .text-link': () => {
					appDisplay.dialog(
						new ServerErrorDetailsDialog({
							title: i18n.get('common.error.technicalDetails'),
							banner: i18n.get(tableMessage),
							stackTrace
						})
					)
				}
			}
		}

		let data = [[{
			html: tableMessageTemplate({ extraClass, message, stackTrace }),
			colspan,
			style: 'has-data-message',
			events
		}]]
		this._addDataRows(structure, data)
	}

	_propagateEvents(emitter, events) {
		_.each(events, name => {
			if (emitter.listeners && _.isEmpty(emitter.listeners(name))) {
				emitter.on(name, (...args) => this.emit(name, ...args))
			}
		})
	}

	_selectTbodySection(sectionType, groupIndexKey) {
		return this._tbodies.filter(function(d, i) {
			let matchsGrouping = true
			if (!_.isNil(groupIndexKey)) {
				matchsGrouping = this.dataset.groupIndexKey == groupIndexKey
			}
			return this.classList.contains(`${sectionType}`) && matchsGrouping
		})
	}

	/**
	 * Replaces the contents of the table with loading dots
	 */
	loading() {
		this.loadingDots.render()
	}

	selection() {
		return this._table
	}

	structure() {
		return this._tableStructure
	}

	header(groupIndexKey = reportConstants.DEFAULT_GROUP_INDEX_KEY) {
		return _.get(this.structure(), `[${groupIndexKey}].header`, null)
	}

	stacks(groupIndexKey, stackIndex) {
		let header = this.header(groupIndexKey)
		if (header) {
			return header.stacks(stackIndex)
		}
		return []
	}

	data(groupIndexKey = reportConstants.DEFAULT_GROUP_INDEX_KEY) {
		return _.get(this.structure(), `[${groupIndexKey}].data`, [])
	}

	dimensions() {
		return this.selection().node().getBoundingClientRect()
	}

	remove() {
		this.loadingDots.remove()
		this.nonAbbreviatedValuesTooltip.remove()
		_.invoke(this._tableDnd, 'remove')
	}

}

export default Table
export { LAST_STACK_ADDABLE_CLASS }
export { DND_HEADER_CLASS }

Table.__filename__ = 'reports/common/table/table.js'
